import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

interface VariationRow {
    variationLabel: string;
    attribute: string;
    baseValue: unknown;
    override: unknown;
}

interface VariationGroup {
    variationLabel: string;
    rows: VariationRow[];
}

@Component({
    selector: 'emm-variations-dialog',
    templateUrl: './variations-dialog.component.html',
    styleUrls: ['./variations-dialog.component.scss'],
    standalone: false
})
export class VariationsDialogComponent implements OnInit {
    item: any;
    variations: any[] = [];
    groupedVariations: VariationGroup[] = [];

    constructor(
        public dialogRef: MatDialogRef<VariationsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { item: any; variations: any[] }
    ) {}

    ngOnInit(): void {
        this.item = this.data.item;
        this.variations = this.data.variations || [];
        this.groupByVariation();
    }

    private prepareTableData(): VariationRow[] {
        const tableData: VariationRow[] = [];
        this.variations?.forEach((variation: any) => {
            const overrides = variation?.overrides || {};
            const nonComponentKeys = Object.keys(overrides).filter((k) => k !== 'components');
            nonComponentKeys.forEach((key) => {
                tableData.push({
                    variationLabel: variation.variationLabel ?? variation.variationTag ?? 'Unlabeled',
                    attribute: key,
                    baseValue: this.getBaseValue(key),
                    override: overrides[key],
                });
            });
            if (nonComponentKeys.length === 0 && variation?.overrides?.components) {
                tableData.push({
                    variationLabel: variation.variationLabel ?? variation.variationTag ?? 'Unlabeled',
                    attribute: 'components',
                    baseValue: this.getBaseValue('components'),
                    override: 'This variation has different set of components',
                });
            }
        });

        return tableData;
    }

    private getBaseValue(key: string) {
        return (
            this.item?.attributes?.find((attr: any) => attr.jsonProperty === key)?.value ??
            this.item?.[key] ?? 'N/A'
        );
    }

    private groupByVariation(): void {
        const rows = this.prepareTableData();
        const map = new Map<string, VariationRow[]>();

        for (const row of rows) {
            const key = row.variationLabel ?? 'Unlabeled';
            if (!map.has(key)) map.set(key, []);
            map.get(key)?.push(row);
        }

        this.groupedVariations = Array.from(map.entries()).map(
            ([variationLabel, groupRows]) => ({
                variationLabel,
                rows: groupRows,
            })
        );
    }

    trackByVariation(index: number, group: VariationGroup) {
        return group.variationLabel;
    }
}
